/**
 * Escudo padrão de time: formato, padrão e duas cores.
 *
 * O capitão escolhe um destes no cadastro (ou fica com o que o nome do time
 * sorteia), e a escolha é gravada no time. Daí em diante o escudo é daquele
 * clube: não muda se o nome mudar, e só troca quando o capitão trocar.
 *
 * ── Onde fica gravado ────────────────────────────────────────────────────
 * No mesmo campo do escudo por imagem (`teams.crest_url`), como um código:
 *
 *     escudo:v1;f=classico;p=listras;c=c8102e,ffffff
 *
 * Todas as rotas que devolvem um time já devolvem esse campo, então o escudo
 * escolhido aparece em qualquer tela sem uma coluna nova nem uma consulta a
 * mais. Quem desenha reconhece o prefixo e monta o escudo; o que não tem o
 * prefixo continua sendo o endereço de uma imagem.
 *
 * O `v1` existe para o dia em que o código ganhar campos: um leitor novo sabe
 * ler os escudos antigos, e um código que ele não conhece cai no escudo do
 * nome em vez de quebrar a tela.
 */
export const ESCUDO_FORMATOS = ['classico', 'ingles', 'moderno', 'ondas', 'medalhao'];
export const ESCUDO_PADROES = ['listras', 'faixa', 'diagonal', 'meio', 'chevron', 'quartos', 'liso'];
/**
 * Pares de clube para o escudo automático: [fundo, detalhe]. Dois tons
 * aleatórios raramente parecem uniforme de futebol; estes parecem.
 */
export const ESCUDO_PARES = [
    ['#c8102e', '#ffffff'], // vermelho e branco
    ['#111111', '#ffffff'], // preto e branco
    ['#0b2a6f', '#f2c200'], // azul-marinho e amarelo
    ['#006b3f', '#ffffff'], // verde e branco
    ['#5b0e2d', '#8ec5ff'], // grená e celeste
    ['#f26b1d', '#111111'], // laranja e preto
    ['#6a1b9a', '#ffffff'], // roxo e branco
    ['#8ec5ff', '#0b2a6f'], // celeste e marinho
    ['#111111', '#c8102e'], // preto e vermelho
    ['#f2c200', '#0a4d2e'], // amarelo e verde
    ['#1d4ed8', '#ffffff'], // azul e branco
    ['#7a1f1f', '#e8c170'], // bordô e dourado
    ['#0f766e', '#f5f5f5'], // petróleo e gelo
    ['#ffffff', '#c8102e'], // branco e vermelho
    ['#1f2937', '#c5d200'], // grafite e lima
    ['#b91c1c', '#111111'], // vermelho e preto
];
/** As cores que o capitão pode escolher, uma a uma. */
export const ESCUDO_CORES = [
    '#ffffff', '#f5f5f5', '#111111', '#1f2937',
    '#c8102e', '#b91c1c', '#7a1f1f', '#5b0e2d',
    '#f26b1d', '#f2c200', '#e8c170', '#c5d200',
    '#006b3f', '#0a4d2e', '#0f766e', '#8ec5ff',
    '#1d4ed8', '#0b2a6f', '#6a1b9a', '#ec4899',
];
export const PREFIXO_ESCUDO = 'escudo:';
function hash(texto, sal = 0) {
    let h = 2166136261 ^ sal;
    for (let i = 0; i < texto.length; i += 1) {
        h ^= texto.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
/**
 * O escudo que o nome do time sorteia. É o ponto de partida do seletor e o
 * escudo gravado quando o capitão não escolhe nenhum.
 */
export function escudoDoNome(nome) {
    const [fundo, detalhe] = ESCUDO_PARES[hash(nome, 1) % ESCUDO_PARES.length];
    return {
        formato: ESCUDO_FORMATOS[hash(nome, 2) % ESCUDO_FORMATOS.length],
        padrao: ESCUDO_PADROES[hash(nome, 3) % ESCUDO_PADROES.length],
        fundo,
        detalhe,
    };
}
export function codificarEscudo(e) {
    return `${PREFIXO_ESCUDO}v1;f=${e.formato};p=${e.padrao};c=${e.fundo.slice(1)},${e.detalhe.slice(1)}`;
}
export function ehEscudoPadrao(valor) {
    return typeof valor === 'string' && valor.startsWith(PREFIXO_ESCUDO);
}
/**
 * Lê um código de escudo. Qualquer coisa fora do formato — campo que não
 * existe, cor que não é hexadecimal, formato desconhecido — devolve null:
 * este valor vem do banco e, na gravação, do navegador, e nenhum dos dois
 * lados deve conseguir desenhar algo que a tela não saiba desenhar.
 */
export function lerEscudo(valor) {
    if (!ehEscudoPadrao(valor))
        return null;
    const partes = valor.slice(PREFIXO_ESCUDO.length).split(';');
    if (partes[0] !== 'v1')
        return null;
    const campos = new Map(partes.slice(1).map((p) => {
        const i = p.indexOf('=');
        return [p.slice(0, i), p.slice(i + 1)];
    }));
    const formato = campos.get('f');
    const padrao = campos.get('p');
    const cores = (campos.get('c') ?? '').split(',');
    if (!formato || !ESCUDO_FORMATOS.includes(formato))
        return null;
    if (!padrao || !ESCUDO_PADROES.includes(padrao))
        return null;
    if (cores.length !== 2 || !cores.every((c) => /^[0-9a-f]{6}$/i.test(c)))
        return null;
    return { formato, padrao, fundo: `#${cores[0].toLowerCase()}`, detalhe: `#${cores[1].toLowerCase()}` };
}
//# sourceMappingURL=escudo.js.map